import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import ReusableButton from './Button/Button';
import CrossIcon from '../../assets/CrossIcon';
import CheckIcon from '../../assets/CheckIcon';
import RedCrossIcon from '../../assets/icon/RedCrossIcon';
import { translate } from '../../i18n';
import { IForecastDetails } from '../../type/forecastDetails';
import { toast } from 'react-toastify';
import operationAPI from '../../api/operationApi';
import { IForcastOptions } from '../../type/forcastOptions';
import { IForecastEntryOption } from '../../type/forecastEntryOption';
import ReusableSelect from './Select';
import { encodeDateForUrl } from '../../utils/dateUtils';
import { IOperationsForecastRequest } from '../../type/IOperationsForecastRequest';
import { CUSTOM_TOAST_ID } from '../../constants/constant';
import { showOrReplaceToast } from '../../utils/utility';
import CrossIconDarkForm from '../../assets/icon/CrossIconForm';

interface WeatherFormProps {
  data: IForecastDetails;
  statusOptions: IForecastEntryOption | undefined;
  closeModal: () => void;
  onConfirm: (data: any) => void;
  ownerId: number;
}

const WeatherForm: React.FC<WeatherFormProps> = ({
  data,
  statusOptions,
  closeModal,
  onConfirm,
  ownerId,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      heading: data?.headlineId,
      expectedTemp: data?.expectedTempId,
      ofl: data?.ofl || '',
      forecastStatus: data?.forecastExpectedId,
      information: data?.whatToBringId,
      notes: data?.note,
    },
  });

  const onSubmit = async (formData: any) => {
    const operationDate = new Date(data?.operationDate);
    const encodedDate = encodeDateForUrl(operationDate);
    const payload: IOperationsForecastRequest = {
      headline: formData.heading,
      expectedTemp: formData.expectedTemp,
      oflTime: formData.ofl,
      forecastExpected: formData.forecastStatus,
      whatToBring: formData.information,
      note: formData.notes,
      operationsDate: encodedDate,
      ownerId: ownerId,
    };

    try {
      setIsLoading(true);
      const response = await operationAPI.updateForcastTravel(payload);
      closeModal();
      toast.success(
        translate('operation.weatherTravelStatus.statusUpdatedSuccessfully'),
      );
      onConfirm(payload);
    } catch (error) {
      showOrReplaceToast(
        CUSTOM_TOAST_ID.operation,
        'Submission failed',
        'error',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleHeadlineChange = (selectedOption: IForcastOptions | null) => {
    if (selectedOption) {
      console.log(selectedOption);
    }
  };

  const operationDate = data?.operationDate
    ? new Date(data.operationDate)
    : null;

  const formattedDate = operationDate
    ? `${operationDate.toLocaleString('default', { weekday: 'short' })}, ${operationDate.getDate()} ${operationDate.toLocaleString('default', { month: 'short' })}`
    : '';

  return (
    <div className="w-auto md:max-w-lg mx-auto  bg-sidebar-background p-6 rounded-lg shadow-md relative">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-base font-semibold text-textColor">
          {translate('weatherAndTravel.formTitle', { date: formattedDate })}
        </h2>
        <button onClick={closeModal}>
          <RedCrossIcon />
        </button>
      </div>
      <div className="border-b-2 border-[#EEF7FF] mb-4"></div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Heading */}
        <div>
          <label className="block text-sm text-start text-text-primary font-medium text-gray-700">
            {translate('weatherAndTravel.heading')}
          </label>
          <ReusableSelect<IForcastOptions>
            options={statusOptions?.headline || ([] as IForcastOptions[])}
            className={`w-full border border-customBorder-border rounded p-2 text-sm  bg-backgroundColor-darkThemeColor text-text-primary `}
            getOptionLabel={(option: IForcastOptions) => option.entryOption}
            getOptionValue={(option: IForcastOptions) => option.optionId}
            onChange={(value) => {
              setValue('heading', value?.optionId);
            }}
            placeholder="Select a headline"
            value={watch('heading')}
          />
          {errors.heading && (
            <p className="text-red-500 text-start text-xs mt-1">
              {errors.heading.message}
            </p>
          )}
        </div>

        {/* Expected Temperature and OFL */}
        <div className="grid grid-cols-12 gap-4">
          {/* Expected Temperature */}
          <div className="col-span-6">
            <label className="block text-sm font-medium text-start text-textColorGray mb-1">
              {translate('weatherAndTravel.expectedTemp')}
            </label>
            <ReusableSelect<IForcastOptions>
              options={statusOptions?.expectedTemp || ([] as IForcastOptions[])}
              className="w-full border border-customBorder-border rounded-md p-2 text-sm  bg-backgroundColor-darkThemeColor text-text-primary"
              getOptionLabel={(option: IForcastOptions) => option.entryOption}
              getOptionValue={(option: IForcastOptions) => option.optionId}
              onChange={(value) => {
                setValue('expectedTemp', value?.optionId);
              }}
              placeholder="Select a temp"
              value={watch('expectedTemp')}
            />
            {errors.expectedTemp && (
              <p className="text-red-500 text-start text-xs mt-1">
                {errors.expectedTemp.message}
              </p>
            )}
          </div>

          {/* OFL input with AM */}
          <div className="col-span-6">
            <label className="block text-sm font-medium text-start text-textColorGray mb-1">
              {translate('weatherAndTravel.ofl')}
            </label>
            <div className="flex w-full">
              <input
                type="time"
                {...register('ofl', { required: 'OFL is required' })}
                className="flex-grow min-w-0 p-2 text-sm border border-customBorder-border rounded-l-md  bg-backgroundColor-darkThemeColor text-text-primary"
              />
              <span className="flex justify-center items-center rounded-r-md  border border-customBorder-border px-3 text-sm text-textColorGray h-10 ml-2">
                AM
              </span>
            </div>
            {errors.ofl && (
              <p className="text-red-500 text-start text-xs mt-1">
                {errors.ofl.message}
              </p>
            )}
          </div>
        </div>

        {/* Forecast or Status */}
        <div>
          <label className="block text-sm text-start font-medium text-text-primary">
            {translate('weatherAndTravel.forcastOrStatus')}
          </label>
          <ReusableSelect<IForcastOptions>
            options={
              statusOptions?.expectedForecast || ([] as IForcastOptions[])
            }
            className={`w-full border border-customBorder-border rounded p-2 text-sm  bg-backgroundColor-darkThemeColor text-text-primary`}
            getOptionLabel={(option: IForcastOptions) => option.entryOption}
            getOptionValue={(option: IForcastOptions) => option.optionId}
            onChange={(value) => {
              setValue('forecastStatus', value?.optionId);
            }}
            placeholder="Select a temp"
            value={watch('forecastStatus')}
          />
          {errors.forecastStatus && (
            <p className="text-red-500 text-start text-xs mt-1">
              {errors.forecastStatus.message}
            </p>
          )}
        </div>

        {/* Information */}
        <div>
          <label className="block text-sm font-medium text-start text-text-primary">
            {translate('weatherAndTravel.information')}
          </label>
          <ReusableSelect<IForcastOptions>
            options={statusOptions?.whatTOBring || ([] as IForcastOptions[])}
            className={`w-full border border-customBorder-border rounded p-2 text-sm   bg-backgroundColor-darkThemeColor text-text-primary`}
            getOptionLabel={(option: IForcastOptions) => option.entryOption}
            getOptionValue={(option: IForcastOptions) => option.optionId}
            onChange={(value) => {
              setValue('information', value?.optionId);
            }}
            placeholder="Select a information"
            value={watch('information')}
          />
          {errors.information && (
            <p className="text-red-500 text-start text-xs mt-1">
              {errors.information.message}
            </p>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-start text-text-primary">
            {translate('weatherAndTravel.note')}
          </label>
          <textarea
            rows={3}
            {...register('notes')}
            className="mt-1 block w-full p-2 text-sm border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-primary"
          />
        </div>

        {/* Buttons */}
        <div className="flex justify-end space-x-4 mt-6">
          <ReusableButton
                variant="secondary"
                children={'Cancel'}
                icon={
                  <div>
                    <div className="block dark:hidden">
                      <CrossIcon />
                    </div>
                    <div className="hidden dark:block">
                      <CrossIconDarkForm />
                    </div>
                  </div>
                }
                onClick={closeModal}
                isDisabled={isLoading}
                className="w-auto bg-button-cancelButtonBgColor text-text-cancelButtonTextColor px-6"
              />
              <ReusableButton
                icon={<CheckIcon />}
                children={'Save'}
                variant="primary"
                className="w-auto bg-background-backgroundColor px-6"
                isDisabled={isLoading}
                isLoader={isLoading}
              />
        </div>
      </form>
    </div>
  );
};

export default WeatherForm;
